/* eslint-disable react/jsx-no-comment-textnodes */
import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTING } from '@constants/routing';
import styled, { css } from 'styled-components';

/**
 * PublicShell — wraps a public-facing page (Pricing, etc.) with the same
 * navbar + footer style as the landing page so visitors keep their bearings.
 *
 * The landing page itself uses its own inline copy of these — keep both in
 * sync if you change branding, footer columns, etc.
 */

const T = {
  bg: '#0a0e14',
  bgSecondary: '#0f1419',
  border: '#2a3544',
  borderHover: '#3d4f66',
  text: '#f0f4f8',
  textSecondary: '#94a3b8',
  textTertiary: '#64748b',
  cyan: '#00c8e0',
  cyan600: '#00a3b8',
  cyan700: '#007d8f',
  font: {
    body: '"Outfit", "DM Sans", system-ui, sans-serif',
    mono: '"JetBrains Mono", "Fira Code", monospace',
  },
};

const Page = styled.div`
  min-height: 100vh;
  background: ${T.bg};
  color: ${T.text};
  font-family: ${T.font.body};
  display: flex;
  flex-direction: column;
`;

const Nav = styled.nav<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 clamp(1rem, 6vw, 6rem);
  height: 76px;
  transition: all 0.3s ease;

  ${p =>
    p.$scrolled
      ? css`
          background: rgba(10, 14, 20, 0.92);
          backdrop-filter: blur(16px);
          border-bottom: 1px solid ${T.border};
        `
      : css`
          background: rgba(10, 14, 20, 0.7);
          backdrop-filter: blur(8px);
        `}
`;

const Logo = styled.div`
  font-family: ${T.font.mono};
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: ${T.text};
  cursor: pointer;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2.25rem;

  @media (max-width: 1023px) {
    display: none;
  }
`;

const NavLink = styled.a<{ $active?: boolean }>`
  color: ${p => (p.$active ? T.text : T.textSecondary)};
  text-decoration: none;
  font-size: 1.05rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${T.text};
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 1023px) {
    display: none;
  }
`;

const BtnGhost = styled.button`
  background: transparent;
  border: 1px solid ${T.border};
  color: ${T.textSecondary};
  padding: 0.65rem 1.6rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  font-family: ${T.font.body};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${T.borderHover};
    color: ${T.text};
    background: rgba(255, 255, 255, 0.02);
  }
`;

const BtnPrimary = styled.button`
  background: linear-gradient(135deg, ${T.cyan600} 0%, ${T.cyan700} 100%);
  border: 1px solid ${T.cyan};
  color: #fff;
  padding: 0.65rem 1.75rem;
  font-size: 0.925rem;
  font-weight: 600;
  font-family: ${T.font.body};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.25s;
  box-shadow:
    0 0 20px rgba(0, 200, 224, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);

  &:hover {
    background: linear-gradient(135deg, ${T.cyan} 0%, ${T.cyan600} 100%);
    transform: translateY(-1px);
    box-shadow:
      0 0 30px rgba(0, 200, 224, 0.4),
      0 8px 24px rgba(0, 0, 0, 0.3);
  }
`;

const MobileMenuBtn = styled.button`
  display: none;
  background: transparent;
  border: 1px solid ${T.border};
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  color: ${T.text};
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;

  @media (max-width: 1023px) {
    display: flex;
  }
`;

const MobileOverlay = styled.div<{ $open: boolean }>`
  display: none;

  @media (max-width: 1023px) {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 99;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    opacity: ${p => (p.$open ? 1 : 0)};
    pointer-events: ${p => (p.$open ? 'auto' : 'none')};
    transition: opacity 0.3s ease;
  }
`;

const MobileMenu = styled.div<{ $open: boolean }>`
  display: none;

  @media (max-width: 1023px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    z-index: 101;
    width: 280px;
    max-width: 85vw;
    height: 100vh;
    background: ${T.bgSecondary};
    border-left: 1px solid ${T.border};
    padding: 1.5rem;
    gap: 0.5rem;
    transform: translateX(${p => (p.$open ? '0' : '100%')});
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow-y: auto;
  }
`;

const MobileMenuClose = styled.button`
  align-self: flex-end;
  background: transparent;
  border: 1px solid ${T.border};
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  color: ${T.text};
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 1.25rem;
`;

const MobileNavLink = styled.a`
  display: block;
  color: ${T.textSecondary};
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 500;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  cursor: pointer;
  min-height: 44px;

  &:hover,
  &:active {
    color: ${T.text};
    background: rgba(0, 200, 224, 0.06);
  }
`;

const MobileMenuDivider = styled.div`
  height: 1px;
  background: ${T.border};
  margin: 0.75rem 0;
`;

const MobileMenuActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const Main = styled.main`
  flex: 1 0 auto;
  padding-top: 76px; /* clear fixed nav */
`;

/* ── Footer ─────────────────────────────────────── */

const Footer = styled.footer`
  flex: 0 0 auto;
  border-top: 1px solid ${T.border};
  padding: 3rem clamp(1.5rem, 5vw, 4rem) 2rem;
  max-width: 1140px;
  margin: 4rem auto 0;
  width: 100%;
  box-sizing: border-box;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr repeat(3, 1fr);
  gap: 3rem;
  margin-bottom: 2.5rem;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FooterBrand = styled.div`
  max-width: 280px;
`;

const FooterBrandName = styled.div`
  font-family: ${T.font.mono};
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: ${T.text};
  margin-bottom: 0.75rem;
`;

const FooterBrandDesc = styled.p`
  font-size: 0.85rem;
  line-height: 1.6;
  color: ${T.textTertiary};
  margin: 0;
`;

const FooterCol = styled.div``;

const FooterColTitle = styled.h4`
  font-family: ${T.font.body};
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${T.textSecondary};
  margin: 0 0 0.85rem;
`;

const FooterLink = styled.a`
  display: block;
  font-size: 0.85rem;
  color: ${T.textTertiary};
  text-decoration: none;
  margin-bottom: 0.55rem;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${T.cyan};
  }
`;

const FooterBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 2rem;
  border-top: 1px solid ${T.border};
  font-size: 0.78rem;
  color: ${T.textTertiary};

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

interface PublicShellProps {
  children: ReactNode;
  /** Active nav link id ('features' | 'how-it-works' | 'results' | 'pricing'). */
  activeNav?: 'features' | 'how-it-works' | 'results' | 'pricing';
}

export default function PublicShell({ children, activeNav }: PublicShellProps) {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const goHome = () => navigate('/');
  const goSection = (id: string) => {
    setMobileMenuOpen(false);
    navigate(`/${ROUTING.LANDING}#${id}`);
  };
  const go = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  return (
    <Page>
      <Nav $scrolled={scrolled} role="navigation" aria-label="Main navigation">
        <Logo onClick={goHome} aria-label="Scrapeking home">
          SCRAPEKING
        </Logo>

        <NavLinks>
          <NavLink onClick={() => goSection('features')} $active={activeNav === 'features'}>
            Features
          </NavLink>
          <NavLink onClick={() => goSection('how-it-works')} $active={activeNav === 'how-it-works'}>
            How It Works
          </NavLink>
          <NavLink onClick={() => goSection('results')} $active={activeNav === 'results'}>
            Results
          </NavLink>
          <NavLink onClick={() => navigate(`/${ROUTING.PRICING}`)} $active={activeNav === 'pricing'}>
            Pricing
          </NavLink>
        </NavLinks>

        <NavActions>
          <BtnGhost onClick={() => navigate(`/${ROUTING.LOGIN}`)}>Log In</BtnGhost>
          <BtnPrimary onClick={() => navigate(`/${ROUTING.SIGNUP}`)}>Get Started Free</BtnPrimary>
        </NavActions>

        <MobileMenuBtn onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </MobileMenuBtn>
      </Nav>

      <MobileOverlay $open={mobileMenuOpen} onClick={() => setMobileMenuOpen(false)} />
      <MobileMenu $open={mobileMenuOpen} aria-label="Mobile navigation" role="dialog">
        <MobileMenuClose onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
          &times;
        </MobileMenuClose>
        <MobileNavLink onClick={() => goSection('features')}>Features</MobileNavLink>
        <MobileNavLink onClick={() => goSection('how-it-works')}>How It Works</MobileNavLink>
        <MobileNavLink onClick={() => goSection('results')}>Results</MobileNavLink>
        <MobileNavLink onClick={() => go(`/${ROUTING.PRICING}`)}>Pricing</MobileNavLink>
        <MobileMenuDivider />
        <MobileMenuActions>
          <BtnGhost onClick={() => go(`/${ROUTING.LOGIN}`)} style={{ width: '100%' }}>
            Log In
          </BtnGhost>
          <BtnPrimary onClick={() => go(`/${ROUTING.SIGNUP}`)} style={{ width: '100%' }}>
            Get Started Free
          </BtnPrimary>
        </MobileMenuActions>
      </MobileMenu>

      <Main>{children}</Main>

      <Footer>
        <FooterGrid>
          <FooterBrand>
            <FooterBrandName>SCRAPEKING</FooterBrandName>
            <FooterBrandDesc>
              The all-in-one lead generation and email outreach platform. Scrape leads, warm up accounts, and send
              campaigns — built for teams that move fast.
            </FooterBrandDesc>
          </FooterBrand>

          <FooterCol>
            <FooterColTitle>Product</FooterColTitle>
            <FooterLink onClick={() => goSection('features')}>Lead Scraping</FooterLink>
            <FooterLink onClick={() => goSection('features')}>Email Warmup</FooterLink>
            <FooterLink onClick={() => goSection('features')}>Campaigns</FooterLink>
            <FooterLink onClick={() => navigate(`/${ROUTING.PRICING}`)}>Pricing</FooterLink>
          </FooterCol>

          <FooterCol>
            <FooterColTitle>Resources</FooterColTitle>
            <FooterLink>Documentation</FooterLink>
            <FooterLink>API Reference</FooterLink>
            <FooterLink>Blog</FooterLink>
            <FooterLink>Changelog</FooterLink>
          </FooterCol>

          <FooterCol>
            <FooterColTitle>Company</FooterColTitle>
            <FooterLink>About</FooterLink>
            <FooterLink>Contact</FooterLink>
            <FooterLink onClick={() => navigate(`/${ROUTING.PRIVACY_POLICY}`)}>Privacy Policy</FooterLink>
            <FooterLink onClick={() => navigate(`/${ROUTING.TERMS_OF_SERVICE}`)}>Terms of Service</FooterLink>
          </FooterCol>
        </FooterGrid>

        <FooterBottom>
          <span>&copy; {new Date().getFullYear()} Scrapeking. All rights reserved.</span>
          <span>Built for deliverability.</span>
        </FooterBottom>
      </Footer>
    </Page>
  );
}
